import { trackCall } from "@/lib/track";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Calendar, Clock, Car, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

const TIMES = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00",
  "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00",
  "15:30", "16:00", "16:30",
];

const STATUS_LABEL = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

const STATUS_TONE = {
  pending: "border-yellow-700 text-yellow-300",
  confirmed: "border-green-700 text-green-300",
  in_progress: "border-orange-600 text-orange-300",
  completed: "border-zinc-700 text-zinc-400",
  cancelled: "border-red-800 text-red-400",
  no_show: "border-red-900 text-red-400",
};

export default function ManageBookingPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("09:00");
  const [fullSlots, setFullSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/manage/${token}`);
      setBooking(data);
      setNewDate(data.preferred_date);
      setNewTime(data.preferred_time);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Booking not found.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!newDate) return;
    api.get(`/appointments/slots`, { params: { date: newDate } })
      .then((r) => setFullSlots(r.data?.full_slots || []))
      .catch(() => setFullSlots([]));
  }, [newDate]);

  const reschedule = async () => {
    if (!newDate || !newTime) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/bookings/manage/${token}`, {
        preferred_date: newDate,
        preferred_time: newTime,
      });
      setBooking(data);
      toast.success("Booking rescheduled — we'll confirm the new time shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reschedule failed");
      if (err.response?.status === 409) {
        try {
          const r = await api.get(`/appointments/slots`, { params: { date: newDate } });
          setFullSlots(r.data?.full_slots || []);
        } catch (slotErr) {
          console.warn("Failed to refresh full slots after 409", slotErr);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/bookings/manage/${token}/cancel`);
      setBooking(data);
      setConfirmingCancel(false);
      toast.success("Booking cancelled. Sorry to see you go.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-black">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading booking…</div>
      </div>
    );
  }
  if (error || !booking) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-black px-6">
        <div className="text-center max-w-md">
          <XCircle className="h-10 w-10 text-brand-red mx-auto" />
          <h1 className="mt-4 font-display text-3xl uppercase tracking-tight text-white">Link not found</h1>
          <p className="mt-2 text-sm text-zinc-400">{error || "This link may have expired or been used already."}</p>
          <Link to="/book" className="mt-6 inline-block px-6 py-3 bg-brand-red text-white font-mono text-[11px] uppercase tracking-widest hover:bg-[#B91C2B]">
            Book a new appointment
          </Link>
        </div>
      </div>
    );
  }

  const readonly = ["cancelled", "completed", "no_show"].includes(booking.status);

  return (
    <div className="bg-black min-h-[80vh] py-16">
      <Toaster position="top-right" richColors closeButton />
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-brand-red">
          <ArrowLeft className="h-3 w-3" /> Back to site
        </Link>

        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Manage your booking</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl uppercase tracking-tight text-white">
            Hi, {booking.customer_name.split(" ")[0]}.
          </h1>
        </div>

        <div className="mt-8 bg-zinc-950 border border-zinc-900 p-6" data-testid="manage-current">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Current booking</div>
              <h2 className="mt-2 font-display text-2xl uppercase tracking-tight text-white">{booking.service_title}</h2>
              <p className="text-sm text-zinc-400 mt-1">
                <Car className="inline h-3.5 w-3.5 mr-1 text-brand-red" />
                {booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}
              </p>
            </div>
            <span
              data-testid="manage-status"
              className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${STATUS_TONE[booking.status] || STATUS_TONE.pending}`}
            >
              {STATUS_LABEL[booking.status] || booking.status}
            </span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 pt-5 border-t border-zinc-900">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Date
              </div>
              <div className="mt-1 text-white text-lg">{booking.preferred_date}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Time
              </div>
              <div className="mt-1 text-white text-lg">{booking.preferred_time}</div>
            </div>
          </div>
        </div>

        {readonly ? (
          <div className="mt-6 bg-zinc-950 border border-zinc-900 p-6 text-center">
            <p className="text-zinc-400 text-sm">
              This booking is <strong className="text-white">{STATUS_LABEL[booking.status]}</strong> and can&apos;t be modified online.
            </p>
            <Link
              to="/book"
              data-testid="manage-book-new"
              className="mt-4 inline-block px-6 py-3 bg-brand-red text-white font-mono text-[11px] uppercase tracking-widest hover:bg-[#B91C2B]"
            >
              Book a new appointment
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-6 bg-zinc-950 border border-zinc-900 p-6" data-testid="manage-reschedule">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-red">Reschedule</div>
              <h3 className="mt-2 font-display text-xl uppercase tracking-tight text-white">Pick a new day &amp; time</h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">New date</label>
                  <input
                    data-testid="manage-new-date"
                    type="date"
                    min={new Date().toISOString().slice(0, 10)}
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="w-full bg-black border border-zinc-800 text-white px-3 py-2.5 focus:border-brand-red outline-none font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">New time</label>
                  <select
                    data-testid="manage-new-time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="w-full bg-black border border-zinc-800 text-white px-3 py-2.5 focus:border-brand-red outline-none font-mono text-sm"
                  >
                    {TIMES.map((t) => {
                      const full = fullSlots.includes(t) && t !== booking.preferred_time;
                      return (
                        <option key={t} value={t} disabled={full}>
                          {t}{full ? " — fully booked" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <button
                data-testid="manage-save-reschedule"
                onClick={reschedule}
                disabled={saving || (newDate === booking.preferred_date && newTime === booking.preferred_time)}
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white font-mono text-[11px] uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {saving ? "Saving…" : "Save new time"}
              </button>
              <p className="mt-2 text-[11px] text-zinc-500">
                We&apos;ll re-confirm the new time by phone or email.
              </p>
            </div>

            <div className="mt-6 bg-zinc-950 border border-zinc-900 p-6" data-testid="manage-cancel-block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Can&apos;t make it?</div>
              <h3 className="mt-2 font-display text-xl uppercase tracking-tight text-white">Cancel booking</h3>
              <p className="mt-2 text-sm text-zinc-400">
                No fee, no hassle. Just let us know and we&apos;ll free the slot for someone else.
              </p>
              {!confirmingCancel ? (
                <button
                  data-testid="manage-cancel-btn"
                  onClick={() => setConfirmingCancel(true)}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 border border-red-800 text-red-400 font-mono text-[11px] uppercase tracking-widest hover:bg-red-950/40"
                >
                  <XCircle className="h-3.5 w-3.5" /> Cancel this booking
                </button>
              ) : (
                <div className="mt-4 flex gap-2 items-center flex-wrap">
                  <span className="text-sm text-white">Are you sure?</span>
                  <button
                    data-testid="manage-cancel-confirm"
                    onClick={cancel}
                    disabled={saving}
                    className="px-4 py-2 bg-red-700 text-white font-mono text-[11px] uppercase tracking-widest hover:bg-red-800 disabled:opacity-40"
                  >
                    Yes, cancel it
                  </button>
                  <button
                    onClick={() => setConfirmingCancel(false)}
                    className="px-4 py-2 border border-zinc-700 text-zinc-300 font-mono text-[11px] uppercase tracking-widest hover:border-zinc-500"
                  >
                    Keep booking
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        <div className="mt-8 text-center">
          <p className="text-xs text-zinc-500">
            Need help? Call us on <a href="tel:0362887861" onClick={() => trackCall("manage_booking")} className="text-brand-red hover:underline">(03) 6288 7861</a> —
            Mon-Fri 8-5, Sat 9-5, Sun 10-5.
          </p>
        </div>
      </div>
    </div>
  );
}
